import { DEFAULT_MAP_ZOOM } from "src/components/Map/constants";
import { urlConfig } from "src/utils/urlConfig";
import type { TicketableSchedulesPassengerDetails } from "../PassengerDetailsProvider";
import type { SupportedCurrencyCode } from "../utils/currency";
import type { SupportedLanguageCode } from "../utils/language";
import { buildUrl } from "../utils/url";
import type { ApiConfig } from "./ApiConfig";

export function searchEndpoint(
  config: ApiConfig,
  origin: string | undefined,
  originGooglePlaceId: string | undefined,
  destination: string | undefined,
  destinationGooglePlaceId: string | undefined,
  language: SupportedLanguageCode,
  currencyCode: SupportedCurrencyCode,
  oPos?: string,
  dPos?: string,
  lightweightRequest = false, // rome2rio-core/OpenAPI/docs/SearchApi.md
) {
  return buildUrl("/api/1.5/json/search", urlConfig.apiHost, {
    key: import.meta.env.VITE_API_KEY,
    oName: origin,
    oGooglePlaceId: originGooglePlaceId,
    dName: destination,
    dGooglePlaceId: destinationGooglePlaceId,
    oPos,
    dPos,
    languageCode: language,
    currencyCode: currencyCode,
    uid: config.uid,
    aqid: config.aqid,
    analytics: !!config.uid,
    debugFeatures: config.backendFeatures,
    debugExperiments: config.backendExperiments,
    flags: config.backendFlags,
    groupOperators: true,
    noAir: lightweightRequest,
    noPrice: lightweightRequest,
  });
}

export function geocodeCanonicalEndpoint(
  config: ApiConfig,
  canonicalName: string | undefined,
  language: SupportedLanguageCode,
) {
  return buildUrl("/api/1.6/canonical", urlConfig.apiHost, {
    key: import.meta.env.VITE_API_KEY,
    canonicalName,
    languageCode: language,
  });
}

export function schedulesEndpoint(
  config: ApiConfig,
  searchData: string,
  oDateTime: string, // ISO
  languageCode: SupportedLanguageCode,
  oneDayOnly?: boolean,
  iDateTime?: string, // ISO
  currencyCode?: string, // ISO
  requestId?: string,
) {
  return buildUrl("/api/1.5/json/schedules", urlConfig.apiHost, {
    key: import.meta.env.VITE_API_KEY,
    searchData,
    languageCode,
    oDateTime,
    iDateTime,
    uid: config.uid,
    aqid: config.aqid,
    debugFeatures: config.backendFeatures,
    debugExperiments: config.backendExperiments,
    flags: config.backendFlags,
    maxForwardSearchDays: oneDayOnly ? 1 : undefined,
    useIndicativePrices: true,
    currencyCode,
    requestId,
  });
}

export function ticketableSchedulesEndpoint(
  config: ApiConfig,
  searchData: string,
  oDateTime: string, // ISO
  languageCode: SupportedLanguageCode,
  currencyCode: SupportedCurrencyCode,
  isReturn: boolean,
  passengerDetails?: TicketableSchedulesPassengerDetails,
  iDateTime?: string, // ISO
  outboundSelectionId?: string,
  requestId?: string,
  extendScheduleId?: string,
  extendDirection?: "outbound" | "inbound",
  extendTo?: "earlier" | "later",
) {
  return buildUrl("/api/1.5/json/ticketableschedules", urlConfig.apiHost, {
    key: import.meta.env.VITE_API_KEY,
    searchData,
    languageCode,
    oDateTime,
    iDateTime,
    uid: config.uid,
    aqid: config.aqid,
    debugFeatures: config.backendFeatures,
    debugExperiments: config.backendExperiments,
    flags: config.backendFlags,
    currencyCode,
    paxAges: passengerDetails ? passengerDetails.paxAges : "",
    paxCards: passengerDetails ? passengerDetails.paxCards : "",
    paxTypes: passengerDetails ? passengerDetails.paxTypes : "",
    outboundSelection: outboundSelectionId,
    allowLazyReturnSearch: isReturn,
    requestId,
    extendScheduleId: extendScheduleId,
    extendDirection: extendDirection,
    extendTo: extendTo,
  });
}

// This is used for searching Static Connected Schedules
export function connectedSchedulesEndpoint(
  config: ApiConfig,
  routeData: string,
  oDateTime: string, // ISO
  languageCode: SupportedLanguageCode,
  oneDayOnly?: boolean,
  requestId?: string,
) {
  return buildUrl("/api/1.5/json/ConnectedSchedule", urlConfig.apiHost, {
    key: import.meta.env.VITE_API_KEY,
    routeData,
    languageCode,
    oDateTime,
    uid: config.uid,
    aqid: config.aqid,
    debugFeatures: config.backendFeatures,
    debugExperiments: config.backendExperiments,
    flags: config.backendFlags,
    pruneSchedules: true,
    schedulesResponse: true,
    maxForwardSearchDays: oneDayOnly ? 1 : undefined,
    useIndicativePrices: true,
    requestId,
  });
}

export function flightSchedulesEndpoint(
  config: ApiConfig,
  origin: string,
  destination: string,
  languageCode: SupportedLanguageCode,
  currencyCode: SupportedCurrencyCode,
  departureDateTime?: string,
  passengerDetails?: TicketableSchedulesPassengerDetails,
  returnDateTime?: string,
  requestId?: string,
) {
  let flightSchedulesRequest = {
    key: import.meta.env.VITE_API_KEY,
    oName: origin,
    dName: destination,
    languageCode,
    currencyCode,
    uid: config.uid,
    aqid: config.aqid,
    debugFeatures: config.backendFeatures,
    debugExperiments: config.backendExperiments,
    flags: config.backendFlags,
    requestId,
  };

  return buildUrl("/api/1.5/json/flightSchedules", urlConfig.apiHost, {
    ...flightSchedulesRequest,
    oDateTime: departureDateTime,
    iDateTime: returnDateTime,
    paxAges: passengerDetails ? passengerDetails.paxAges : "",
    paxCards: passengerDetails ? passengerDetails.paxCards : "",
    paxTypes: passengerDetails ? passengerDetails.paxTypes : "",
  });
}

export function hotelListEndpoint(
  config: ApiConfig,
  canonicalName: string | undefined,
  lat: number | undefined,
  lng: number | undefined,
  googlePlaceId: string | undefined,
  arrivalDate: string | undefined, // YYYY-MM-DD,
  departureDate: string | undefined, // YYYY-MM-DD,
  languageCode: SupportedLanguageCode,
  currencyCode: SupportedCurrencyCode,
  zoom = DEFAULT_MAP_ZOOM,
  radius = 25,
) {
  // RoomsData is included in POST body.
  return buildUrl("/api/1.6/hotels/hotelList", urlConfig.apiHost, {
    key: import.meta.env.VITE_API_KEY,
    pos: lat && lng ? `${lat},${lng}` : undefined,
    languageCode,
    currencyCode,
    name: canonicalName,
    googlePlaceId,
    arrivalDate,
    departureDate,
    uid: config.uid,
    aqid: config.aqid,
    debugFeatures: config.backendFeatures,
    debugExperiments: config.backendExperiments,
    zoom, // Zoom is required because the default zoom doesn't return any results with minPrice > 0
    radius,
    flags: config.backendFlags,
  });
}
export function logExitEndpoint(config: ApiConfig) {
  return buildUrl("/api/json/LogExit", urlConfig.apiHost, {
    uid: config.uid,
    aqid: config.aqid,
  });
}
